// import Median from 'median-js-bridge';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { ApolloError } from '@apollo/client';
import LocationArrowSVG from '../../assets/imgs/icon/location-arrow.svg';

import { Button } from '../Button';
import { Container } from '../Container';
import { Flex } from '../Flex';
import { Input } from '../Input';
import { storeIdState } from '../../Store/atoms';
import {
	StoreFieldsFragment,
	useStoresByCityLazyQuery,
	useStoresByCoordinatesLazyQuery,
	useStoresByZipCodeLazyQuery
} from '../../Store/graphql';
// import { on_store_zipcode } from '../../utils/gtag';
// import { StorePickupFieldsFragment } from '../../graphql';

interface IZipCodeField {
	onStores: (store: StoreFieldsFragment[]) => void;
	onLoading: (loading: boolean) => void;
	onSubmitted: (submitted: boolean) => void;
	onRequestError: (error?: ApolloError) => void;
	onSearchValue: (zipCode: number | string) => void;
}

export const ZipCodeField = ({
	onStores,
	onLoading,
	onRequestError,
	onSubmitted,
	onSearchValue
}: IZipCodeField): JSX.Element => {
	const [validationError, setValidationError] = useState('');
	const [searchValue, setSearchValue] = useState<string>('');
	const storeId = useRecoilValue(storeIdState);
	let latitude: number;
	let longitude: number;

	const success = (position: GeolocationPosition) => {
		latitude = position.coords.latitude;
		longitude = position.coords.longitude;
		setValidationError('');
		setSearchValue('');
		onSubmitted(true);
		storeSearch();
	};

	const error = (geolocationError: GeolocationPositionError) => {
		if (geolocationError.code === 1 || geolocationError.code === 2) {
			setValidationError('Please check if location services is enabled');
		}
	};

	// const gtmUpdate = (
	// 	stores: (StoreFieldsFragment & StorePickupFieldsFragment)[] | undefined
	// ) => {
	// 	if (Array.isArray(stores) && stores.length > 0 && stores[0].zipCode)
	// //		on_store_zipcode({ store_zip: Number(stores[0].zipCode) });
	// };

	const [storesByCoordinates, { loading: coordinatesLoading }] =
		useStoresByCoordinatesLazyQuery({
			fetchPolicy: 'network-only',
			onCompleted({ storesByCoordinates }) {
				// gtmUpdate(storesByCoordinates);
				const stores = [...storesByCoordinates];
				stores.some((store, index) => {
					if (store.storeId === storeId) {
						stores.splice(index, 1);
						stores.unshift(store);
					}
					return null;
				});
				onStores(stores);
			},
			onError(error) {
				onRequestError(error);
			}
		});

	const [storesByZipCode, { loading: zipLoading }] =
		useStoresByZipCodeLazyQuery({
			fetchPolicy: 'network-only',
			onCompleted({ storesByZipCode }) {
				const stores = [...storesByZipCode];
				stores.some((store, index) => {
					if (store.storeId === storeId) {
						stores.splice(index, 1);
						stores.unshift(store);
					}
					return null;
				});
				onStores(stores);
			},
			onError(error) {
				onRequestError(error);
			}
		});

	const [storesByCity, { loading: cityLoading }] = useStoresByCityLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted({ storesByCity }) {
			const stores = [...storesByCity];
			stores.some((store, index) => {
				if (store.storeId === storeId) {
					stores.splice(index, 1);
					stores.unshift(store);
				}
				return null;
			});
			onStores(stores);
		},
		onError(error) {
			onRequestError(error);
		}
	});

	onLoading(zipLoading || coordinatesLoading || cityLoading);

	const storeSearch = () => {
		setValidationError('');
		if (searchValue) {
			storesByZipCode({ variables: { zip: parseInt(searchValue) } });
			//	on_store_zipcode({ store_zip: parseInt(searchValue) });
		} else {
			// let latitudePayload, longitudePayload;
			if (latitude === null || longitude === null) {
				setValidationError(
					'Please make sure location services are enabled'
				);

				return;
			}

			storesByCoordinates({
				fetchPolicy: 'network-only',
				variables: {
					latitude,
					longitude
				}
			});
		}
	};

	const getLocation = () => {
		navigator.geolocation.getCurrentPosition(success, error, {
			enableHighAccuracy: true,
			timeout: 1000,
			maximumAge: 10000
		});
	};

	// const median_geolocation_ready = () => {
	// 	Median.android.geoLocation.promptLocationServices();
	// };

	const handleLocationSearch = () => {
		// if (!navigator.userAgent.includes('MedianIOS')) {
		// 	median_geolocation_ready();
		// }
		getLocation();
	};

	const searchStore = () => {
		if (!searchValue) {
			return setValidationError('*Please enter the ZIP code or City.');
		} else if (
			/^\d+$/.test(searchValue) &&
			(searchValue.length < 5 || searchValue.length > 5)
		) {
			return setValidationError('*Please check the ZIP code again.');
		}

		setValidationError('');
		onSearchValue(searchValue);

		if (/^\d+$/.test(searchValue) && searchValue.length === 5) {
			const zip = Number(searchValue);
			if (zip) {
				onSubmitted(true);
				// on_store_zipcode({ store_zip: zip });
				storesByZipCode({ variables: { zip } });
			}
		} else {
			const city = searchValue.trim();
			if (city) {
				onSubmitted(true);
				storesByCity({ variables: { city } });
			}
		}
	};

	const handleValue = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;
		setSearchValue(inputValue);
	};
	return (
		<Container>
			<Flex
				$width="100%"
				$flexDirection="row"
				$justifyContent="space-between"
				$columnGap="10px"
			>
				<Input
					name="ZipCode"
					value={searchValue}
					placeholder="ZIP/City"
					onChange={handleValue}
				></Input>
				<Button
					$buttonSize="small"
					onClick={searchStore}
					$isLoading={!!searchValue && zipLoading}
					disabled={zipLoading}
					$minWidth="90px"
				>
					SEARCH
				</Button>
				<Button
					$buttonSize="small"
					onClick={handleLocationSearch}
					$minWidth="90px"
					$isLoading={coordinatesLoading}
				>
					<img
						src={LocationArrowSVG}
						style={{ width: '14px', height: '14px' }}
					/>
				</Button>
			</Flex>
			<Flex
				$columnGap="8px"
				$alignItems="center"
				$margin="0 0 16px 0"
				$width="100%"
			>
				{validationError && (
					<ErrorMessage>{validationError}</ErrorMessage>
				)}
			</Flex>
		</Container>
	);
};

const ErrorMessage = styled.span`
	margin-top: 8px;
	color: #dc3545;
	font-size: 14px;
	font-weight: 600;
`;
